<template>
  <nav
    class="NavWrapper flex align-center"
    aria-label="Hoofdmenu navigatie"
    :class="scrollState"
  >
    <ul
      class="flex main-nav-wrapper flex-nowrap align-center"
      role="list"
    >
      <UIHeaderNavItem
        v-for="nav in mainNav"
        :key="nav.id"
        class="sm-hide"
        :navigation="nav"
        :add-top-level-child="false"
        :clickable-parent="false"
      />
    </ul>

    <div class="action-menu flex align-center ml-xlg sm-ml-0">
      <div class="relative accessibility-menu-wrapper sm-hide">
        <UIIconButton
          data-accessibility-menu-toggle="true"
          :custom-icon="'accessibility'"
          :label="accessibilityIsExpanded ? 'Sluit toegankelijkheidsmenu' : 'Open toegankelijkheidsmenu'"
          :aria-expanded="accessibilityIsExpanded"
          :aria-controls="accessibilityMenuId"
          class="btn-ghost m-0 small accessibility-menu-btn"
          :class="[{ active: accessibilityIsExpanded }]"
          @click="accessibilityIsExpanded = !accessibilityIsExpanded"
        />
      </div>
      <UIIconButton
        :custom-icon="'shop'"
        :label="'Ga naar de winkelwagen'"
        :link="'/winkelmand'"
        class="open-menu-btn btn-ghost m-0 small"
      />
      <UIIconButton
        :custom-icon="'search'"
        :label="searchIsExpanded ? 'Sluit zoekmenu' : 'Open zoekmenu'"
        :aria-expanded="searchIsExpanded"
        :aria-controls="searchDialogId"
        no-animation
        class="open-menu-btn btn-ghost m-0 small"
        :class="[{ active: searchIsExpanded }]"
        @click="searchIsExpanded = !searchIsExpanded"
      />

      <div class="relative sm-show hide">
        <UIIconButton
          :custom-icon="mobileNavIcon"
          :label="mobileIsExpanded ? 'Sluit menu' : 'Open menu'"
          :aria-expanded="mobileIsExpanded"
          :aria-controls="mobileNavId"
          no-animation
          class="open-menu-btn btn-ghost m-0 no-animation small"
          @click="mobileIsExpanded = !mobileIsExpanded"
        />
        <div class="sm-show hide">
          <UIHeaderMobileMenuWrapper
            :id="mobileNavId"
            :is-open="mobileIsExpanded"
          />
        </div>
      </div>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <UISearchNavigationWrapper
          :id="searchDialogId"
          v-model="searchIsExpanded"
        />
      </Teleport>
    </ClientOnly>
  </nav>
</template>

<script lang="ts" setup>
  import type { Navigation } from '@tc8l/nuxt-base'

  const { scrollState } = useScrollDirection()

  const searchDialogId = useId()
  const mobileNavId = useId()
  const accessibilityMenuId = 'accessibility-menu-panel'

  const mainNav = ref<Navigation[]>(await baseGetNavigationByGroup(10))

  const mobileNavIcon = computed(() => (mobileIsExpanded.value ? 'close' : 'menu'))
  const mobileIsExpanded = ref(false)

  const hamburgerIsExpanded = ref(false)

  const searchIsExpanded = ref(false)
  const accessibilityIsExpanded = useState<boolean>('accessibility-menu-expanded', () => false)

  function closeMobileMenu() {
    mobileIsExpanded.value = false
  }

  function closeHamburgerMenu() {
    hamburgerIsExpanded.value = false
  }

  function handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && mobileIsExpanded.value) {
      mobileIsExpanded.value = false
    }

    if (event.key === 'Escape' && hamburgerIsExpanded.value) {
      hamburgerIsExpanded.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleEscapeKey)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleEscapeKey)
  })

  function resetMenu() {
    closeHamburgerMenu()
    closeMobileMenu()
    searchIsExpanded.value = false
    accessibilityIsExpanded.value = false
  }

  const router = useRouter()
  router.afterEach(resetMenu)
</script>

<style lang="scss" scoped>
  .NavWrapper {
    z-index: 150;
    border-radius: 100rem;
    color: $color-text;

    .main-nav-wrapper {
      gap: var(--text-heading-5);
    }

    .hamburger {
      right: 0;
      left: unset;
      transform: unset;
      transform: translateX(4rem);
    }
  }

  .action-menu {
    gap: var(--padding-sm);
    button,
    a {
      &.active {
        transform: scale(1.2);
      }
    }
  }

  @include media(sm) {
    .action-menu {
      gap: 1.6rem;

      button,
      a {
        flex-shrink: 0;
      }
    }
  }
</style>
